import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import * as Promise from 'bluebird';
import { IAjax } from 'lib/Ajax';
import { AutoSaver } from 'lib/autoSave';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { DraftObject } from 'lib/draftSignals';
import { Mutex } from 'lib/mutex';

import { Coordination } from 'data/models/Coordination';
import { CoordinationCurrency } from 'server/enums/CoordinationCurrency';
import { Domain, DomainOptions } from 'data/models/Domain';
import { MilestoneType } from 'data/models/MilestoneType';
import { ReorderCoordinationsRequestData } from 'data/models/ReorderCoordinationsRequest';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';

export interface CoordinationsOverviewParams {
    // Nothing for now
}

export type CoordinationsOverview = ReturnType<typeof CoordinationsOverview>;
export function CoordinationsOverview(
    loader : CachedModelLoader<Domain, DomainOptions>,
    domain : Domain,
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    _params : CoordinationsOverviewParams
) {
    const
        Coordinations = domain.Coordinations
            .map(c => CoordinationViewModel(c, ajax))
            .sort((c1, c2) => Coordination.DisplayOrder(c1.Model, c2.Model)),
        MilestoneTypes = lift(() => Array.from(data.MilestoneTypes.items()))
            .orderBy(t => t.DisplayOrder()),
        initialDataLoad = loader({ Id: S.sample(domain.Id) }),
        displayOrderSaver = DisplayOrderSaver(data, ajax, entrypoints, Coordinations);

    return {
        Coordinations,
        MilestoneTypes,
        Loading: () => initialDataLoad.loading() || displayOrderSaver.SavingDisplayOrder() || Coordinations().some(c => c.SavingCurrency()),
        OnSortCoordinations
    };

    function OnSortCoordinations(_movedCoordination : CoordinationViewModel, newOrder : CoordinationViewModel[]) {
        S.freeze(() => newOrder.forEach((c, i) => c.Model.DisplayOrder(i)));
    }
}

function DisplayOrderSaver(
    data : PCCache,
    ajax : IAjax,
    entrypoints : Entrypoints,
    Coordinations : ArraySignal<CoordinationViewModel>
) {
    const
        client = ajax.Url<ReorderCoordinationsRequestData, DomainOptions>(entrypoints.ReorderCoordinations),
        persistedOrder = Coordinations.sort((c1, c2) => Coordination.DisplayOrder(c1.Persisted, c2.Persisted)),
        draftOrder = Coordinations.sort((c1, c2) => Coordination.DisplayOrder(c1.Model, c2.Model)),
        // Counter that increases monotonically whever there's a discrepancy between persisted order and draft
        // order.
        orderChangeCount = S.value(0),
        SavingDisplayOrder = S.value(false);

    S(lastCount => orderChangeCount(lastCount + (arrayEq(persistedOrder(), draftOrder()) ? 0 : 1)), S.sample(orderChangeCount));

    AutoSaver(orderChangeCount, () => {
        SavingDisplayOrder(true);
        const checkpoints = draftOrder().map(c => c.DisplayOrderCheckpoint());
        return Promise.all(draftOrder().map(c => c.SaveMutex.acquire())).then(guards => Promise.resolve()
            .then(() => {
                const req = client.Post({
                    Coordinations: draftOrder().map(c => ({ Id: c.Model.Id() }))
                });
                return req.then(domainData => S.freeze(() => {
                    data.Domains.update1(req.Date, domainData);
                    checkpoints.forEach(c => c.revert());
                }));
            })
            .finally(() => guards.forEach(guard => guard.release()))
        ).finally(() => SavingDisplayOrder(false));
    }, 0);

    return { SavingDisplayOrder };
}

export type CoordinationViewModel = ReturnType<typeof CoordinationViewModel>;
function CoordinationViewModel(
    coordination : Coordination,
    ajax : IAjax
) {
    const
        // DisplayOrder and Currency in their own DraftObjects as we'll handle persisting them separately.
        displayOrderDraftObj = DraftObject(coordination, ['DisplayOrder']),
        displayOrderDraft = displayOrderDraftObj.object,
        currencyDraftObj = DraftObject(displayOrderDraft, ['Currency']),
        currencyDraft = currencyDraftObj.object,
        client = ajax.Url<Coordination, unknown>(() => coordination.Url() ?? ''),
        // A mutex to serialize any attempts to persist new DisplayOrder or Currency values. This isn't so much
        // to avoid server-side races -- we've partitioned our server-side code such that DisplayOrder and
        // Currency edits are handled by separate endpoints that avoid modifying each other's properties.
        // Instead, it's about avoiding client-side races updating our cached Coordination model after a
        // successful save.
        SaveMutex = new Mutex(),
        ShowActionsMenu = S.value(false),
        SavingCurrency = S.value(false);

    AutoSaver(currencyDraftObj.methods.onWrite, () => {
        SavingCurrency(true);
        const checkpoint = currencyDraftObj.methods.checkpoint();
        return SaveMutex.acquire().then(guard => Promise.resolve()
            .then(() => client.Put(currencyDraft).then(() => checkpoint.commit()))
            // Revert in case of errors to allow retrying
            .catch(err => { checkpoint.revert(); throw err; })
            .finally(() => guard.release())
        ).finally(() => SavingCurrency(false));
    }, 0);

    return {
        // displayOrderDraft and NOT currencyDraft. We won't update the displayed currency value in the main
        // table until that value has been persisted.
        Model: displayOrderDraft,
        Persisted: coordination,
        SaveMutex,
        ShowActionsMenu,
        SavingCurrency,
        DisplayOrderCheckpoint: () => displayOrderDraftObj.methods.checkpoint(),
        ApplicableForMilestoneType,
        MenuAction,
        ToggleCurrency
    };

    function ApplicableForMilestoneType(t : MilestoneType) {
        return coordination.MilestoneTypes().some(ct => ct.MilestoneType() === t);
    }

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            ShowActionsMenu(false);
            action();
        });
    }

    function ToggleCurrency() {
        // TODO: A nice-to-have here would be detecting if the user is trying to restore a mandatory coordination
        // and if so spawning a confirmation popup to make sure they know that this might auto-create C-Cards.
        currencyDraft.Currency(S.sample(coordination.Currency) === CoordinationCurrency.Current ? CoordinationCurrency.Deprecated : CoordinationCurrency.Current);
    }
}

function arrayEq<T>(a1 : T[], a2 : T[]) {
    if (a1.length !== a2.length) return false;
    for (let i = 0; i < a1.length; i++) {
        if (a1[i] !== a2[i]) return false;
    }
    return true;
}



// WEBPACK FOOTER //
// ./apps/portcall/coordinationsoverview/model.ts